"use client";

import type React from "react";
import { Skeleton } from "@/components/ui";
import { usePriceConverter } from "@/hooks/price/usePriceConverter";

interface ProductPriceProps {
  seller_share_unit?: number | string | null;
  seller_share_currency?: string | null;
  seller_settlement_currency?: string | null;
  bv?: number | string | null;
  className?: string;
  /** Optional commission rate to deduct from SV (e.g. 0.03 for 3%) */
  commissionRate?: number;
}

/**
 * Component to display product price converted to seller settlement currency
 * Shows: BV UVM + SV in settlement currency (USDT or UVM)
 */
export const ProductPrice = ({
  seller_share_unit,
  seller_share_currency,
  seller_settlement_currency,
  bv,
  className,
  commissionRate,
}: ProductPriceProps): React.ReactNode => {
  const priceConverter = usePriceConverter();

  if (!seller_share_unit || !seller_share_currency) return null;

  // Normalize settlement currency - default to UVM if not provided
  const normalizeSettlement = (
    currency: string | null | undefined,
  ): "USDT" | "UVM" => {
    if (!currency) return "UVM";
    const normalized = currency.toUpperCase();
    if (normalized === "USDT") return "USDT";
    return "UVM";
  };
  const settlementCurrency = normalizeSettlement(seller_settlement_currency);

  if (priceConverter.isLoading || !priceConverter.rates) {
    return <Skeleton variant="muted" className={className || "h-4 w-24"} />;
  }

  // Convert price to settlement currency
  const { svInSettlementCurrency } = priceConverter.convertToSettlementCurrency(
    {
      seller_share_unit,
      seller_share_currency,
      bv,
    },
    settlementCurrency,
  );

  const bvValue = typeof bv === "string" ? parseFloat(bv) : bv || 0;
  const hasBv = bvValue > 0;

  // Apply commission deduction if provided
  const finalSv = commissionRate
    ? svInSettlementCurrency * (1 - commissionRate)
    : svInSettlementCurrency;

  // Format SV with smart decimal display
  const formattedSv = (() => {
    const rounded = Math.round(finalSv * 100) / 100;
    const decimalPart = rounded % 1;
    const formattedValue =
      decimalPart === 0 || decimalPart < 0.1
        ? Math.round(rounded).toString()
        : rounded.toFixed(2);
    return formattedValue;
  })();

  // Format BV with smart decimal display
  const formattedBv = (() => {
    const rounded = Math.round(bvValue * 100) / 100;
    const decimalPart = rounded % 1;
    return decimalPart === 0 || decimalPart < 0.1
      ? Math.round(rounded).toString()
      : rounded.toFixed(2);
  })();

  return (
    <div
      dir="ltr"
      className={`flex items-center gap-0.5 font-inter ${className || ""}`}
    >
      <span>{formattedSv}</span>
      <span>{settlementCurrency}</span>
      {hasBv && (
        <>
          <span>+</span>
          <span>{formattedBv}</span>
          <span>UVM</span>
        </>
      )}
    </div>
  );
};
